import Swal from "sweetalert2";
import styled from 'styled-components';
import EditIcon from 'mdi-react/EditIcon';
import { Spinner } from 'react-bootstrap';
import DeleteIcon from 'mdi-react/DeleteIcon';
import DownloadIcon from 'mdi-react/DownloadIcon';
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import FileDocumentBoxRemoveIcon from 'mdi-react/FileDocumentBoxRemoveIcon';
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';
import { Card, CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  &:hover svg {
    fill: ${colorGreenHover};
  }

  &:hover svg.orange-hover {
    fill: #e9c886;
  }

  &:hover svg.red-hover {
    fill: #e98686;
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const MissionsList = (props) => {
    const [showLoader, setShowLoader] = useState(true);
    const [missionsList, setMissionsList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: false,
        withSearchEngine: true,
        placeholder: 'Search...',
    };

    const handleDownloadCentroids = (mission) => {
        Swal.fire({
            title: 'Info!',
            text: `Please wait until we are preparing the ${mission.name} centroids for download. Download starts automatically.`,
            icon: 'info',
            showConfirmButton: false
        });
        AdminInterface.instance.downloadMissionCentroids(mission.id).then((response) => {
            let filename = response.data['filename']
            const url = response.data['download_url']
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        }).catch((error) => {
            Swal.fire({
                title: 'Error',
                icon: 'error',
                html: `Downloading the mission centroids is not possible. ${error.response?.data?.detail || error.message || ''}`,
            });
        });
    }

    const handleEdit = (mission) => {
        props.setSelectedMission(mission)
    }

    const handleDeleteAsset = (mission) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the assets in mission ${mission.name}? This process will delete the files in the database and storage.`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteMissionAssets(mission.id).then((response) => {
                    withReactContent(Swal).fire({
                        title: "Success",
                        html: response.data['message'],
                    });
                    props.setListRequiresReload(true);
                }).catch((error) => {
                    withReactContent(Swal).fire({
                        title: 'Error',
                        icon: 'error',
                        html: `Deleting assets in mission is not possible. ${error.response?.data?.detail || ''}`,
                    });
                })
            }
        });
    }

    const handleDelete = (mission) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the mission ${mission.name}? You will not be able to recover this mission records.`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteMission(mission.id).then(() => {
                    withReactContent(Swal).fire({
                        title: "Success",
                        html: "Mission is deleted!",
                    });
                    props.setListRequiresReload(true);
                }).catch((error) => {
                    withReactContent(Swal).fire({
                        title: 'Error',
                        icon: 'error',
                        html: `Deleting a mission with existing relations is not possible. ${error.response?.data?.detail || ''}`,
                    });
                })
            }
        });
    }

    const GetMissions = () => {
        setShowLoader(true);
        AdminInterface.instance.getMissions().then((response) => {
            const columns = [
                {
                    accessor: 'id',
                    Header: 'Id',
                },
                {
                    accessor: 'name',
                    Header: 'Name',
                },
                {
                    accessor: 'collection_name',
                    Header: 'Collection',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'start_datetime',
                    Header: 'Start Datetime',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'end_datetime',
                    Header: 'End Datetime',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'no_providers',
                    Header: 'No. Providers',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'country',
                    Header: 'Country',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'state',
                    Header: 'State',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'city',
                    Header: 'City',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'centroids',
                    Header: 'Centroids',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'edit',
                    Header: 'Edit',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete_assets',
                    Header: 'Delete Assets',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.forEach(mission => {
                data.push({
                    id: mission.id,
                    name: mission.name,
                    collection_name: mission.collection.name,
                    start_datetime: new Date(`${mission.start_datetime}z`).toLocaleString(),
                    end_datetime: new Date(`${mission.end_datetime}z`).toLocaleString(),
                    no_providers: mission.providers.length,
                    country: mission.country,
                    state: mission.state,
                    city: mission.city,
                    centroids: <CartTableButton type="button" onClick={() => handleDownloadCentroids(mission)}><DownloadIcon /></CartTableButton>,
                    edit: <CartTableButton type="button" onClick={() => handleEdit(mission)}><EditIcon className="orange-hover"/></CartTableButton>,
                    delete_assets: <CartTableButton type="button" onClick={() => handleDeleteAsset(mission)}><FileDocumentBoxRemoveIcon className="red-hover"/></CartTableButton>,
                    delete: <CartTableButton type="button" onClick={() => handleDelete(mission)}><DeleteIcon className="red-hover"/></CartTableButton>,
                });
            });

            setMissionsList({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            withReactContent(Swal).fire({
                title: "Error",
                html: error.response?.data?.detail,
            });
        }).finally(() => {
            setShowLoader(false);
            props.setListRequiresReload(false);
        });
    }

    useEffect(() => {
        GetMissions();
    }, [props.listRequiresReload]);

    return (
        <div style={{ overflowY: 'auto', height: '100%' }}>
            <Card>
                <CardBody>
                    <CardTitleWrap>
                        <CardTitle>Missions List</CardTitle>
                    </CardTitleWrap>
                    {
                        !showLoader &&
                        <ReactTableBase columns={missionsList.tableHeaderData} data={missionsList.tableRowsData} tableConfig={tableConfig} />
                    }
                    {showLoader && (<div className="text-center"><TableSpinner animation="border" /></div>)}
                </CardBody>
            </Card>
        </div>
    );
};

export default MissionsList;